import React from 'react';

// Option 1: Snowflake Cooling Icon (Modern & Clean)
export const LogoOption1 = ({ className = "" }) => {
  return (
    <div className={`flex items-center gap-2 md:gap-3 ${className}`}>
      <div className="relative flex-shrink-0">
        <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-blue-900 via-blue-700 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
          <svg className="w-6 h-6 md:w-7 md:h-7 text-cyan-300" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <line x1="12" y1="2" x2="12" y2="22"></line>
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            <line x1="6" y1="6" x2="9" y2="3"></line>
            <line x1="15" y1="21" x2="18" y2="18"></line>
            <line x1="18" y1="6" x2="15" y2="3"></line>
            <line x1="9" y1="21" x2="6" y2="18"></line>
          </svg>
        </div>
        <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 md:w-3 md:h-3 bg-cyan-400 rounded-full border-2 border-white shadow-md"></div>
      </div>
      <div className="flex flex-col leading-tight">
        <div className="flex items-center gap-1.5">
          <span className="text-xl md:text-2xl lg:text-3xl font-extrabold text-blue-900 tracking-tight">HVAC</span>
          <span className="text-lg md:text-xl lg:text-2xl font-bold text-blue-600">Best</span>
        </div>
        <span className="text-[10px] md:text-xs font-semibold text-gray-600 tracking-widest uppercase mt-0.5">SUPPLY</span>
      </div>
    </div>
  );
};

// Option 2: Shield Badge with HBS Monogram (Professional & Bold)
export const LogoOption2 = ({ className = "" }) => {
  return (
    <div className={`flex items-center gap-2 md:gap-3 ${className}`}>
      <div className="relative flex-shrink-0">
        <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-900 rounded-lg flex items-center justify-center shadow-lg border-2 border-blue-600">
          <div className="text-center">
            <div className="text-xs md:text-sm font-black text-cyan-300 leading-none">HBS</div>
          </div>
        </div>
      </div>
      <div className="flex flex-col leading-tight">
        <div className="flex items-center gap-1">
          <span className="text-xl md:text-2xl lg:text-3xl font-black text-blue-900 tracking-tight">HVAC</span>
          <span className="text-lg md:text-xl lg:text-2xl font-bold text-blue-600">BEST</span>
        </div>
        <span className="text-[10px] md:text-xs font-bold text-gray-600 tracking-wider uppercase">SUPPLY</span>
      </div>
    </div>
  );
};

// Option 3: Circular AC Unit Icon (Technical & Industry-Focused)
export const LogoOption3 = ({ className = "" }) => {
  return (
    <div className={`flex items-center gap-2 md:gap-3 ${className}`}>
      <div className="relative flex-shrink-0">
        <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-blue-800 to-blue-500 rounded-full flex items-center justify-center shadow-lg border-2 border-cyan-300">
          {/* AC Vent Lines */}
          <div className="flex flex-col gap-0.5">
            <div className="w-5 md:w-6 h-0.5 bg-white rounded"></div>
            <div className="w-5 md:w-6 h-0.5 bg-cyan-200 rounded"></div>
            <div className="w-5 md:w-6 h-0.5 bg-white rounded"></div>
            <div className="w-5 md:w-6 h-0.5 bg-cyan-200 rounded"></div>
          </div>
        </div>
      </div>
      <div className="flex flex-col leading-tight">
        <div className="flex items-baseline gap-1.5">
          <span className="text-xl md:text-2xl lg:text-3xl font-extrabold text-blue-900">HVAC</span>
          <span className="text-base md:text-lg lg:text-xl font-semibold text-blue-600">Best Supply</span>
        </div>
      </div>
    </div>
  );
};

// Option 4: Minimalist Modern (Clean & Elegant)
export const LogoOption4 = ({ className = "" }) => {
  return (
    <div className={`flex items-center gap-2 md:gap-3 ${className}`}>
      <div className="relative flex-shrink-0">
        <div className="w-10 h-10 md:w-12 md:h-12 bg-white border-3 border-blue-900 rounded-lg flex items-center justify-center shadow-lg">
          <div className="text-blue-900 font-black text-lg md:text-xl">H</div>
        </div>
      </div>
      <div className="flex flex-col leading-tight">
        <div className="text-xl md:text-2xl lg:text-3xl font-black text-blue-900 tracking-tight">
          HVAC <span className="text-blue-600">BEST</span>
        </div>
        <span className="text-[10px] md:text-xs font-medium text-gray-500 tracking-wider">SUPPLY</span>
      </div>
    </div>
  );
};

// Logo Selector Component - Shows all options
const LogoOptions = ({ selectedOption = 1, onSelect }) => {
  const logos = [
    { id: 1, name: 'Snowflake Cooling', component: LogoOption1, description: 'Modern with cooling icon' },
    { id: 2, name: 'Shield Badge', component: LogoOption2, description: 'Professional monogram' },
    { id: 3, name: 'AC Unit Circle', component: LogoOption3, description: 'Technical & industry-focused' },
    { id: 4, name: 'Minimalist', component: LogoOption4, description: 'Clean & elegant' }
  ];

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-center mb-2">Choose Your Logo</h1>
        <p className="text-center text-gray-600 mb-12">Select your favorite design for HVAC Best Supply</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {logos.map(({ id, name, component: LogoComponent, description }) => (
            <div
              key={id}
              onClick={() => onSelect && onSelect(id)}
              className={`bg-white rounded-xl p-8 shadow-lg cursor-pointer transition-all hover:shadow-2xl hover:scale-105 ${
                selectedOption === id ? 'ring-4 ring-blue-600' : ''
              }`}
            >
              <div className="flex flex-col items-center">
                <div className="mb-6 p-6 bg-gray-50 rounded-lg">
                  <LogoComponent />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Option {id}: {name}</h3>
                <p className="text-sm text-gray-600 text-center mb-4">{description}</p>
                {selectedOption === id && (
                  <div className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                    ✓ Selected
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {selectedOption && (
          <div className="mt-12 text-center">
            <p className="text-lg text-gray-700 mb-4">Preview on header:</p>
            <div className="bg-white p-6 rounded-lg shadow-md inline-block">
              {selectedOption === 1 && <LogoOption1 />}
              {selectedOption === 2 && <LogoOption2 />}
              {selectedOption === 3 && <LogoOption3 />}
              {selectedOption === 4 && <LogoOption4 />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogoOptions;
